import { api } from '../lib/api.js';
import type { Localized, Period } from './learning.js';

/** U-1010-03 (החלטה 16): סיבת החלפת חלף — מובנית, אותם ערכים בשרת. */
export const FAILURE_CAUSES = ['MANUFACTURING', 'DESIGN', 'INSTALLATION', 'SUPPLIER', 'WEAR', 'MISUSE'] as const;
export type FailureCause = (typeof FAILURE_CAUSES)[number];
export const FAILURE_CAUSE_LABEL: Record<FailureCause, string> = {
  MANUFACTURING: 'ייצור', DESIGN: 'תכנון', INSTALLATION: 'התקנה', SUPPLIER: 'חלק של ספק', WEAR: 'בלאי', MISUSE: 'שימוש לא נכון',
};

// ביקור אחזקה בשטח.
//
// אלי: „המשרד שולח לו את הביקור“ · „אם מחליפים פריט חובה לצלם“ ·
// „חתימת לקוח על הטאבלט + PDF במייל“.

export type VisitResult = 'PENDING' | 'DONE' | 'NOT_DONE';
export const RESULT_LABEL: Record<VisitResult, string> = {
  PENDING: 'טרם נענה', DONE: 'בוצע', NOT_DONE: 'לא בוצע',
};

export type VisitItem = {
  id: string;
  result: VisitResult;
  note: string | null;
  minutes: number | null;
  photoPath: string | null;
  /** „צלם — חובה" לביקור הזה (החלטה 143) — ומה לצלם. */
  photoRequired?: boolean;
  photoHint?: Localized;
  /** „דורש חלק" (החלטה 148) — מה סומן; suggestedPartIds = המק״טים מהערת החלפים של הפעולה. */
  needsPart?: { id: string; sku: string; desc: Localized; qty: number } | null;
  suggestedPartIds?: string[];
  openedCallId: string | null;
  replacedPart: { id: string; sku: string; desc: Localized } | null;
  replacedQty: number | null;
  /** U-1010-03: למה החלף הוחלף (MANUFACTURING…MISUSE). */
  failureCause?: FailureCause | null;
  doneAt: string | null;
  plan: {
    id: string; period: Period; periodLabel: string;
    action: Localized; detail: Localized; safety: Localized; partsNote: Localized;
    manualPage: number | null; techCount: number; estMinutes: number | null;
    illustrationId: string | null;
  };
};

export type Visit = {
  id: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'AWAITING_SIGNATURE' | 'CLOSED' | 'CANCELLED';
  statusLabel: string;
  period: Period; periodLabel: string;
  scheduledFor: string | null;
  startedAt: string | null; closedAt: string | null;
  signerName: string | null; signedAt: string | null; hasSignature: boolean;
  /** „אין נציג באתר" — מתי ולמי מנהל השירות שלח קישור חתימה (U-1009-08 part 2). */
  signatureRequestedAt?: string | null; signatureRequestedTo?: string | null;
  technician: { id: string; name: string } | null;
  note: string | null;
  machine: {
    id: string; serialNumber: string; siteName: string | null; poRef: string | null;
    runningHours: number | null;
    /** U-1010-07: המונה כפי שנקרא בביקור הזה. */
    visitRunningHours: number | null;
    customer: { id: string; name: string };
    model: { id: string; manufacturer: string; name: Localized; typeCode: string | null; safety: Localized };
  };
  parts: { id: string; sku: string; level: number; desc: Localized }[];
  progress: { done: number; total: number };
  /** פעולות עם צילום חובה שחסר (החלטה 144). */
  photoMissing?: number;
  /** „מסמכים" — חוברות הדגם + מסמכי ההזמנה שלנו (החלטות 145, 155). */
  docs?: VisitDoc[];
  /** החוברת ש„מדריך · עמ׳ N" פותח. */
  manual?: { id: string; url: string; title: string } | null;
  items: VisitItem[];
};

export type VisitDoc = {
  id: string; source: 'MODEL' | 'ORDER'; kind: string; title: string;
  page: number | null; mime: string | null; url: string;
};

export const visitsApi = {
  get: (id: string, lang?: string) =>
    api<Visit>(`/api/visits/${id}${lang === 'ru' ? '?lang=ru' : ''}`),
  /** הביקור של עבודת שטח — „מה לעשות" בכרטיס הקריאה (13/10/2026). null = אין ביקור. */
  byJob: (jobId: string) => api<Visit | null>(`/api/visits/by-job/${encodeURIComponent(jobId)}`),

  schedule: (body: {
    customerMachineId: string; period: Period;
    technicianId?: string | null; scheduledFor?: string | null; note?: string | null;
    /** U-1009-07: פעולות נבחרות לפי היקף ההזמנה — גוברות על period. */
    planItemIds?: string[] | null;
    /** U-1009-11: ביקור לקריאת אחזקה — השרת מאחד את כל התקופות של הקריאה. */
    callId?: string | null;
    /** U-1010-11: הרכב; לא נשלח = הרכב המשויך לטכנאי, null = בלי רכב חברה. */
    vehicleRefId?: string | null;
    /** חלון „מתי ומי" (החלטה 161): צוות של כמה עם ★, ימים לאיש, עד-תאריך ושעת יציאה. */
    crew?: Array<{ employeeId: string; isLead?: boolean; days?: number | null }>;
    /** „צלם — חובה" לביקור הזה (החלטה 143); null = ברירת המחדל של הספרייה. */
    photoRequiredIds?: string[] | null;
    endDate?: string | null;
    departureTime?: string | null;
  }) => api<{ id: string; itemCount: number; serviceJobId: string }>('/api/visits', { method: 'POST', body }),

  /** „החלף טכנאי / יום" (אלי 12/10/2026) — הביקור וצוות העבודה זזים יחד. */
  reassign: (id: string, body: { technicianId?: string | null; scheduledFor?: string | null }) =>
    api<{ id: string; technicianId: string | null; scheduledFor: string | null; codeMailed: boolean }>(`/api/visits/${id}`, { method: 'PATCH', body }),

  /** U-1010-11: „מה לקחת" — אותה רשימה שהשיבוץ ימלא לעבודה, עם מלאי לפי מק״ט. */
  equipmentPreview: (body: {
    customerMachineId: string; period: Period; planItemIds?: string[] | null; callId?: string | null;
  }) => api<{
    rows: { kind: 'PART' | 'MATERIAL' | 'TOOL'; nameHe: string; nameRu: string | null; sku: string | null; inMachine: number | null; stock: number | null }[];
    notes: string[]; linked: boolean;
  }>('/api/visits/equipment-preview', { method: 'POST', body }),

  // שמירת שורה, תמונה, פתיחת קריאה, סגירה ושעות מונה מהמשרד — נמחקו (החלטה 131):
  // רק VisitPage המת קרא להן. הטכנאי עובד דרך fieldApi.visit*.

  /** „אין נציג באתר" → מנהל השירות שולח ללקוח קישור חתימה בפורטל. email ריק = איש הקשר של הקריאה. */
  requestSignature: (id: string, email?: string, photoItemIds: string[] = []) =>
    api<{ ok: true; to: string; queued: boolean }>(`/api/visits/${id}/request-signature`, {
      method: 'POST',
      // U-1010-01: התמונות שהמנהל סימן לשליחה הזו — ברירת מחדל: אף אחת.
      body: { ...(email ? { email } : {}), ...(photoItemIds.length ? { photoItemIds } : {}) },
    }),

  photoUrl: (itemId: string) => `/api/visits/items/${encodeURIComponent(itemId)}/photo`,

  /** A8: הפעולות של הביקור האחרון למערכת — „לפי היקף ההזמנה" מסמן אותן מראש. */
  lastScope: (machineId: string) =>
    api<{ at: string | null; planItemIds: string[] }>(`/api/visits/machine/${encodeURIComponent(machineId)}/last-scope`),

  history: (machineId: string) =>
    api<Array<{ id: string; period: string; closedAt: string | null; signerName: string | null; technicianName: string | null; itemCount: number }>>(
      `/api/visits/machine/${machineId}/history`),
};
